/**
* Format DICOM date (YYYYMMDD) to "Mmm dd, yyyy"
* @param {string} dicomDate - Date in DICOM format (YYYYMMDD)
* @returns {string} Formatted date (e.g., "Jan 15, 2024")
*/
export const formatDicomDate = (dicomDate) => {
  if (!dicomDate || dicomDate === 'Unknown') return 'Unknown';

  // Handle ISO format (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}$/.test(dicomDate)) {
    const [year, month, day] = dicomDate.split('-');
    const yearNum = parseInt(year);
    const monthNum = parseInt(month);
    const dayNum = parseInt(day);
    if (isNaN(yearNum) || isNaN(monthNum) || isNaN(dayNum)) return 'Unknown';
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[monthNum - 1]} ${dayNum}, ${yearNum}`;
  }

  // Handle DICOM format (YYYYMMDD)
  if (/^\d{8}$/.test(dicomDate)) {
    const year = dicomDate.substring(0, 4);
    const month = dicomDate.substring(4, 6);
    const day = dicomDate.substring(6, 8);
    const yearNum = parseInt(year);
    const monthNum = parseInt(month);
    const dayNum = parseInt(day);
    if (isNaN(yearNum) || isNaN(monthNum) || isNaN(dayNum)) return 'Unknown';
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[monthNum - 1]} ${dayNum}, ${yearNum}`;
  }

  return 'Unknown';
};

/**
* Format DICOM time (HHMMSS.FFFFFF) to "HH:MM:SS"
* @param {string} dicomTime - Time in DICOM format (HHMMSS or HHMMSS.FFFFFF)
* @returns {string} Formatted time (e.g., "14:35:22")
*/
export const formatDicomTime = (dicomTime) => {
  if (!dicomTime || dicomTime === 'Unknown') return 'Unknown';

  // Handle ISO format (HH:MM:SS)
  if (/^\d{2}:\d{2}:\d{2}$/.test(dicomTime)) {
    return dicomTime;
  }

  // Handle DICOM format (HHMMSS or HHMMSS.FFFFFF)
  if (/^\d{6}(\.\d+)?$/.test(dicomTime)) {
    const hours = dicomTime.substring(0, 2);
    const minutes = dicomTime.substring(2, 4);
    const seconds = dicomTime.substring(4, 6);
    return `${hours}:${minutes}:${seconds}`;
  }

  return 'Unknown';
};

/**
* Calculate age from DICOM birth date (YYYYMMDD)
* @param {string} birthDate - Birth date in DICOM format (YYYYMMDD)
* @returns {number|null} Age in years, or null if invalid
*/
/**
* Calculate age from birth date and reference date (e.g., study date)
* @param {string} birthDate - Birth date in DICOM format (YYYYMMDD or YYYY-MM-DD)
* @param {string} referenceDate - Reference date in DICOM format (YYYYMMDD or YYYY-MM-DD)
* @returns {number|null} Age in years at reference date, or null if invalid
*/
export const calculateAge = (birthDate, studyDate) => {
  if (!birthDate || birthDate.length < 8 || !studyDate || studyDate.length < 8) return null;
  try {
    // Parse birth date
    let birthYear, birthMonth, birthDay;
    if (/^\d{8}$/.test(birthDate)) {
      birthYear = parseInt(birthDate.substring(0, 4));
      birthMonth = parseInt(birthDate.substring(4, 6));
      birthDay = parseInt(birthDate.substring(6, 8));
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(birthDate)) {
      const [y, m, d] = birthDate.split('-');
      birthYear = parseInt(y);
      birthMonth = parseInt(m);
      birthDay = parseInt(d);
    } else {
      return null;
    }
    // Parse study date
    let studyYear, studyMonth, studyDay;
    if (/^\d{8}$/.test(studyDate)) {
      studyYear = parseInt(studyDate.substring(0, 4));
      studyMonth = parseInt(studyDate.substring(4, 6));
      studyDay = parseInt(studyDate.substring(6, 8));
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(studyDate)) {
      const [y, m, d] = studyDate.split('-');
      studyYear = parseInt(y);
      studyMonth = parseInt(m);
      studyDay = parseInt(d);
    } else {
      return null;
    }
    if ([birthYear, birthMonth, birthDay, studyYear, studyMonth, studyDay].some(isNaN)) return null;
    let age = studyYear - birthYear;
    if (studyMonth < birthMonth || (studyMonth === birthMonth && studyDay < birthDay)) {
      age--;
    }
    if (age < 0 || age > 150) return null;
    return age;
  } catch {
    return null;
  }
};